import { BaseExportService } from './BaseExportService';

const checkStatusMap = {
  0: 'Failed',
  1: 'Passed',
  2: 'Not Done',
};

/**
 * OCR Liveness Export Service
 * 
 * Handles data export for OCR & Liveness Verification reports
 */
export class OcrLivenessExportService extends BaseExportService {
  constructor(data, metadata = {}) {
    super(data, metadata);
    
    // Extract OCR Liveness data structure
    this.ocr_data = this.data?.ocr_data || {};
    this.liveness_data = this.data?.liveness_data || {};
    this.facial_comparison_data = this.data?.facial_comparison_data || {};
  }

  get_filename_prefix() {
    const timestamp = Date.now().toString().slice(-8);
    return `OCR_Liveness_Verification_${timestamp}`;
  }

  get_available_sections() {
    return {
      personal_information: 'Personal Information',
      document_details: 'Document Details',
      facial_comparison_liveness: 'Facial Comparison & Liveness',
      document_authentication: 'Document Authentication',
      raw_json: 'Raw JSON Data',
    };
  }

  get_default_export_options() {
    return {
      personal_information: false,
      document_details: false,
      facial_comparison_liveness: false,
      document_authentication: false,
      raw_json: false,
    };
  }

  prepare_export_data(export_options) {
    const export_data = {};

    if (export_options.personal_information) {
      export_data.personal_information = this.prepare_personal_information();
    }

    if (export_options.document_details) {
      export_data.document_details = this.prepare_document_details();
    }

    if (export_options.facial_comparison_liveness) {
      export_data.facial_comparison_liveness = this.prepare_facial_comparison_liveness();
    }

    if (export_options.document_authentication) {
      export_data.document_authentication = this.prepare_document_authentication();
    }

    if (export_options.raw_json) {
      export_data.raw_json = [this.prepare_raw_json_data()];
    }

    return export_data;
  }

  /**
   * Prepare personal information from OCR data
   */
  prepare_personal_information() {
    const doc_details = this.ocr_data.document_details || {};
    
    return [{
      'Full Name': doc_details.Full_Name || 'N/A',
      'First Name': doc_details.First_Name || 'N/A',
      'Middle Name': doc_details.Middle_Name || 'N/A',
      'Last Name': doc_details.Last_Name || 'N/A',
      'Date of Birth': doc_details.Date_of_Birth || 'N/A',
      'Gender': doc_details.Gender || 'N/A',
      'Nationality': doc_details.Nationality || 'N/A',
      'ID Number': doc_details.ID_Number || 'N/A',
      'Issue Date': doc_details.Issue_Date || 'N/A',
      'Expiry Date': doc_details.Expiry_Date || 'N/A',
      'Address': doc_details.Address || 'N/A',
    }];
  }

  /**
   * Prepare document details
   */
  prepare_document_details() {
    const doc_type = this.ocr_data.document_type_info || {};
    const doc_details = this.ocr_data.document_details || {};
    
    return [{
      'Document Type': doc_type.document_type || 'N/A',
      'Document Country': doc_type.document_country || 'N/A',
      'Document Format': doc_type.document_format || 'N/A',
      'MRZ Type': doc_details.MRZ_Type || 'N/A',
      'MRZ Line 1': doc_details.MRZ_Line_1 || 'N/A',
      'MRZ Line 2': doc_details.MRZ_Line_2 || 'N/A',
      'MRZ Line 3': doc_details.MRZ_Line_3 || 'N/A',
      'Document Number': doc_details.Document_Number || 'N/A',
      'Personal Number': doc_details.Personal_Number || 'N/A',
      'Place of Birth': doc_details.Place_of_Birth || 'N/A',
      'Place of Issue': doc_details.Place_of_Issue || 'N/A',
    }];
  }

  /**
   * Prepare facial comparison and liveness data
   */
  prepare_facial_comparison_liveness() {
    // Handle both legacy and new API formats
    const facial_interpretation = this.facial_comparison_data.interpretation || {};
    const confidence = facial_interpretation.confidence || this.facial_comparison_data.confidence_level || 'N/A';
    const message = facial_interpretation.message || this.facial_comparison_data.message || 'N/A';
    const recommendation = facial_interpretation.recommendation || this.facial_comparison_data.recommendation || 'N/A';
    
    // Image quality
    const selfie_quality = this.facial_comparison_data.selfieImageQuality?.quality || 
                           this.facial_comparison_data.selfie_quality || 'N/A';
    const doc_quality = this.facial_comparison_data.comparisonImageQuality?.quality || 
                        this.facial_comparison_data.document_quality || 'N/A';
    
    // Liveness results
    const liveness_results = this.liveness_data.results || {};
    const liveness_summary = this.liveness_data.summary || {};
    const quality_assessment = this.liveness_data.quality_assessment || {};
    const screen_detection = this.liveness_data.screen_detection || {};
    const integrated_analysis = this.liveness_data.integrated_analysis || {};
    
    return [{
      'Facial Match Confidence': confidence,
      'Facial Match Message': message,
      'Recommendation': recommendation,
      'Selfie Image Quality': selfie_quality,
      'Document Image Quality': doc_quality,
      'Liveness Status': liveness_results.liveness_status || 'N/A',
      'Liveness Score': liveness_results.liveness_score || 'N/A',
      'Liveness Confidence': liveness_results.confidence || 'N/A',
      'Liveness Decision': liveness_summary.decision || 'N/A',
      'Liveness Summary': liveness_summary.summary || 'N/A',
      'Face Quality Score': quality_assessment.face_quality_score || 'N/A',
      'Face Quality Details': quality_assessment.face_quality_details || 'N/A',
      'Image Brightness': quality_assessment.image_brightness || 'N/A',
      'Image Sharpness': quality_assessment.image_sharpness || 'N/A',
      'Screen Detection Result': screen_detection.screen_detected || 'N/A',
      'Screen Detection Confidence': screen_detection.confidence || 'N/A',
      'Integrated Analysis Status': integrated_analysis.final_status || 'N/A',
      'Integrated Analysis Confidence': integrated_analysis.overall_confidence || 'N/A',
      'Risk Factors': Array.isArray(integrated_analysis.risk_factors) ? 
                      integrated_analysis.risk_factors.join('; ') : 'N/A',
    }];
  }

  /**
   * Prepare document authentication data
   */
  prepare_document_authentication() {
    const doc_auth = this.ocr_data.document_authenticity || {};
    
    return [{
      'MRZ Check': checkStatusMap[doc_auth.MRZ_Check] || 'Unknown',
      'Security Features': checkStatusMap[doc_auth.Security_Features] || 'Unknown',
      'Chip Authentication': checkStatusMap[doc_auth.Chip_Authentication] || 'Unknown',
      'Visual Check': checkStatusMap[doc_auth.Visual_Check] || 'Unknown',
      'Barcode Check': checkStatusMap[doc_auth.Barcode_Check] || 'Unknown',
      'UV Check': checkStatusMap[doc_auth.UV_Check] || 'Unknown',
      'IR Check': checkStatusMap[doc_auth.IR_Check] || 'Unknown',
      'Overall Authentication': checkStatusMap[doc_auth.Overall_Result] || 'Unknown',
    }];
  }

  /**
   * Prepare raw JSON data
   */
  prepare_raw_json_data() {
    return {
      ocr_data: this.ocr_data,
      liveness_data: this.liveness_data,
      facial_comparison_data: this.facial_comparison_data,
    };
  }
}
